import { useState, useEffect } from 'react';
import type {
  Bastion,
  Domain,
  Account,
  CloudflareKey,
  CloudflareZone,
  CloudflareDnsRecord,
  CloudflareApiResponse,
  ApiToken,
  DomainLookup,
  BillingInfo,
  AutoRefillSettings,
  TunnelUsage,
} from './types';

type Listener<T> = (data: T) => void;

class Watcher<T> {
  url: string;
  data: T | null = null;
  listeners: Set<Listener<T>> = new Set();
  intervalId: ReturnType<typeof setInterval> | null = null;
  error: Error | null = null;
  paused: boolean = false;

  constructor(url: string) {
    this.url = url;
  }
}

// Returned to the .catch path internally so callers can branch on rate-limiting,
// but it is intentionally never set on watchers — polling pauses silently and the
// previous cached data stays on screen until the refresh succeeds.
class RateLimitError extends Error {
  retryAfterSeconds: number;
  constructor(retryAfterSeconds: number) {
    super(`rate limited; retry in ${retryAfterSeconds}s`);
    this.name = 'RateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// Parses the Retry-After header. Spec allows seconds or HTTP-date; the backend
// only emits seconds, but we tolerate either. Falls back to 60s if unparseable.
function parseRetryAfter(res: Response): number {
  const raw = res.headers.get('Retry-After');
  if (!raw) return 60;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(raw);
  if (!Number.isNaN(date)) {
    return Math.max(0, Math.ceil((date - Date.now()) / 1000));
  }
  return 60;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

class BastionApi {
  constructor(private client: ApiClient) { }

  list() {
    return this.client.request<Bastion[]>('/api/bastion');
  }

  get(id: string) {
    return this.client.request<Bastion>(`/api/bastion/${id}`);
  }

  getTunnels(id: string, page: number = 1, limit: number = 10) {
    return this.client.request<{ tunnels: TunnelUsage[]; total: number }>(`/api/bastion/${id}/tunnel?page=${page}&limit=${limit}`);
  }

  getUsage(id: string) {
    return this.client.request<{ bytes_tx: number; bytes_rx: number }>(`/api/bastion/${id}/usage`);
  }

  getUsageHistory(id: string, start: string, end: string, bucket: string = 'day') {
    return this.client.request<{ data_points: { time: string; bytes_tx: number; bytes_rx: number }[] }>(`/api/bastion/${id}/usage/history?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&bucket=${bucket}`);
  }

  delete(id: string) {
    return this.client.request(`/api/bastion/${id}`, { method: 'DELETE' });
  }

  update(id: string, data: { suspended?: boolean }) {
    return this.client.request<Bastion>(`/api/bastion/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  getEvents(id: string, start?: string, end?: string) {
    let url = `/api/bastion/${id}/events`;
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    return this.client.request<{ events: BastionEvent[] }>(url);
  }
}

// Types for bastion events
export interface BastionEvent {
  id: number;
  type: 'status_change' | 'tunnel_activity';
  timestamp: string;
  details: StatusChangeDetails | TunnelActivityDetails;
}

export interface StatusChangeDetails {
  status: string;
}

export interface TunnelActivityDetails {
  tunnel_name: string;
  instance_id: string;
  source: string;
  destination: string;
  bytes_tx: number;
  bytes_rx: number;
}

class DomainApi {
  constructor(private client: ApiClient) { }

  list() {
    return this.client.request<Domain[]>('/api/domain');
  }

  add(domain: string) {
    return this.client.request<Domain>(`/api/domain/${domain}`, {
      method: 'PUT',
      body: JSON.stringify({})
    });
  }

  delete(domain: string) {
    return this.client.request(`/api/domain/${domain}`, { method: 'DELETE' });
  }

  lookup(domain: string) {
    return this.client.request<DomainLookup>(`/api/domain/lookup?domain=${domain}`);
  }
}

class AccountApi {
  constructor(private client: ApiClient) { }

  get() {
    return this.client.request<Account>('/api/account');
  }

  update(accountId: string, data: Partial<Account>) {
    return this.client.request<Account>(`/api/account/${encodeURIComponent(accountId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  create(data: { email: string; password: string; name?: string }) {
    return this.client.request<Account & { token?: string }>('/api/account', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  resendConfirmation() {
    return this.client.request('/api/account/resend-confirmation', {
      method: 'POST'
    });
  }
}

class CloudflareApi {
  constructor(private client: ApiClient) { }

  async getZones(keyId: number, name?: string) {
    let url = `/api/cloudflare/zones?key_id=${keyId}`;
    if (name) {
      url += `&name=${encodeURIComponent(name)}`;
    }
    return this.client.request<CloudflareApiResponse<CloudflareZone[]>>(url);
  }

  async getDnsRecords(keyId: number, zoneId: string) {
    return this.client.request<CloudflareApiResponse<CloudflareDnsRecord[]>>(`/api/cloudflare/zones/${zoneId}/dns_records?key_id=${keyId}`);
  }

  async createDnsRecord(keyId: number, zoneId: string, record: Partial<CloudflareDnsRecord>) {
    return this.client.request<CloudflareApiResponse<CloudflareDnsRecord>>(`/api/cloudflare/zones/${zoneId}/dns_records?key_id=${keyId}`, {
      method: 'POST',
      body: JSON.stringify(record)
    });
  }

  async updateDnsRecord(keyId: number, zoneId: string, recordId: string, record: Partial<CloudflareDnsRecord>) {
    return this.client.request<CloudflareApiResponse<CloudflareDnsRecord>>(`/api/cloudflare/zones/${zoneId}/dns_records/${recordId}?key_id=${keyId}`, {
      method: 'PUT',
      body: JSON.stringify(record)
    });
  }

  async deleteDnsRecord(keyId: number, zoneId: string, recordId: string) {
    return this.client.request(`/api/cloudflare/zones/${zoneId}/dns_records/${recordId}?key_id=${keyId}`, {
      method: 'DELETE'
    });
  }
}

class CloudflareKeysApi {
  constructor(private client: ApiClient) { }

  list(accountId: string) {
    return this.client.request<CloudflareKey[]>(`/api/account/${encodeURIComponent(accountId)}/cloudflare-keys`);
  }

  add(accountId: string, name: string, token: string) {
    return this.client.request<CloudflareKey>(`/api/account/${encodeURIComponent(accountId)}/cloudflare-keys`, {
      method: 'POST',
      body: JSON.stringify({ name, token })
    });
  }

  delete(accountId: string, id: number) {
    return this.client.request(`/api/account/${encodeURIComponent(accountId)}/cloudflare-keys/${id}`, { method: 'DELETE' });
  }
}

class ApiTokensApi {
  constructor(private client: ApiClient) { }

  list(accountId: string) {
    return this.client.request<ApiToken[]>(`/api/account/${encodeURIComponent(accountId)}/tokens`);
  }

  create(accountId: string, name: string) {
    return this.client.request<ApiToken & { token: string }>(`/api/account/${encodeURIComponent(accountId)}/tokens`, {
      method: 'POST',
      body: JSON.stringify({ name })
    });
  }

  delete(accountId: string, id: number) {
    return this.client.request(`/api/account/${encodeURIComponent(accountId)}/tokens/${id}`, { method: 'DELETE' });
  }
}

class BillingApi {
  constructor(private client: ApiClient) { }

  get(accountId: string) {
    return this.client.request<BillingInfo>(`/api/account/${encodeURIComponent(accountId)}/billing`);
  }

  addCredits(accountId: string, amountCents: number) {
    return this.client.request<{ clientSecret?: string; type?: 'payment' | 'setup' }>(
      `/api/account/${encodeURIComponent(accountId)}/add-credits`,
      {
        method: 'POST',
        body: JSON.stringify({ amount_cents: amountCents })
      }
    );
  }

  confirmCredits(accountId: string, paymentIntentId: string) {
    return this.client.request(
      `/api/account/${encodeURIComponent(accountId)}/confirm-credits`,
      {
        method: 'POST',
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      }
    );
  }

  updateAutoRefill(accountId: string, settings: AutoRefillSettings) {
    return this.client.request(
      `/api/account/${encodeURIComponent(accountId)}/auto-refill`,
      {
        method: 'PATCH',
        body: JSON.stringify(settings)
      }
    );
  }
}

class ApiClient {
  private watchers = new Map<string, Watcher<any>>();

  public readonly bastion = new BastionApi(this);
  public readonly domain = new DomainApi(this);
  public readonly account = new AccountApi(this);
  public readonly cloudflare = new CloudflareApi(this);
  public readonly cloudflareKeys = new CloudflareKeysApi(this);
  public readonly apiTokens = new ApiTokensApi(this);
  public readonly billing = new BillingApi(this);

  private getToken() {
    return localStorage.getItem('token');
  }

  private async fetcher(url: string) {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, { headers });
    if (res.status === 401) {
      localStorage.removeItem('token');
      // In a real app, we might want a cleaner way to redirect, but this works
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }
    if (res.status === 429) {
      throw new RateLimitError(parseRetryAfter(res));
    }
    if (!res.ok) {
      throw new Error(`Failed to fetch ${url}: ${res.statusText}`);
    }
    return res.json();
  }

  watch<T>(url: string, listener: Listener<T>, options: { poll?: boolean } = { poll: true }): () => void {
    let watcher = this.watchers.get(url);
    if (!watcher) {
      watcher = new Watcher<T>(url);
      this.watchers.set(url, watcher);
      this.startPolling(watcher, options.poll ?? true);
    } else if (options.poll === false && watcher.intervalId) {
      // If watcher exists and is polling, but we requested no polling, we don't stop it
      // because other listeners might want polling.
      // However, if we wanted to be strict, we'd need ref counting on polling vs non-polling listeners.
      // For now, if *any* listener starts a watcher with polling, it polls.
      // If a watcher already exists, we just attach to it.
    }

    watcher.listeners.add(listener);

    // Send current data if available immediately
    if (watcher.data !== null) {
      try {
        listener(watcher.data);
      } catch (e) {
        console.error("Error in watcher listener:", e);
      }
    }

    return () => {
      if (!watcher) return;
      watcher.listeners.delete(listener);
      if (watcher.listeners.size === 0) {
        this.stopPolling(watcher);
        this.watchers.delete(url);
      }
    };
  }

  getCached<T>(url: string): T | null {
    return this.watchers.get(url)?.data || null;
  }

  getError(url: string): Error | null {
    return this.watchers.get(url)?.error || null;
  }

  async refresh(url: string) {
    const watcher = this.watchers.get(url);
    if (watcher) {
      try {
        const data = await this.fetcher(watcher.url);
        watcher.data = data;
        watcher.error = null;
        watcher.listeners.forEach(l => l(data));
      } catch (err: any) {
        if (err instanceof RateLimitError) {
          // Silent: keep the previous data and let the next refresh retry.
          return;
        }
        watcher.error = err;
        console.error(`Refresh error for ${watcher.url}:`, err);
      }
    }
  }

  private async startPolling(watcher: Watcher<any>, poll: boolean) {
    const fetch = async () => {
      try {
        const data = await this.fetcher(watcher.url);
        // Only notify if data changed? For now, simple update.
        // Deep comparison could be expensive, so we trust the server or React to handle diffs.
        watcher.data = data;
        watcher.error = null;
        watcher.listeners.forEach(l => l(data));
      } catch (err: any) {
        if (err instanceof RateLimitError) {
          // Pause this watcher's polling for the server-provided retry window so
          // we stop spending the user's rpm budget on background refreshes. The
          // cached data remains visible; no error is propagated to the UI.
          if (watcher.paused || !poll) return;
          watcher.paused = true;
          if (watcher.intervalId) {
            clearInterval(watcher.intervalId);
            watcher.intervalId = null;
          }
          setTimeout(() => {
            if (this.watchers.get(watcher.url) !== watcher) return; // unsubscribed
            watcher.paused = false;
            fetch();
            watcher.intervalId = setInterval(fetch, 5000);
          }, err.retryAfterSeconds * 1000);
          return;
        }
        watcher.error = err;
        console.error(`Polling error for ${watcher.url}:`, err);
      }
    };

    fetch(); // Initial fetch

    if (poll) {
      watcher.intervalId = setInterval(fetch, 5000); // Poll every 5 seconds
    }
  }

  private stopPolling(watcher: Watcher<any>) {
    if (watcher.intervalId) {
      clearInterval(watcher.intervalId);
      watcher.intervalId = null;
    }
  }

  // Generic fetch for non-watched requests (POST, PUT, DELETE, or one-off GET).
  // On 429, waits the server-provided Retry-After and transparently retries once
  // so user-initiated actions don't surface the rate-limit error.
  async request<T = any>(url: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let res = await fetch(url, { ...options, headers });
    while (res.status === 429) {
      await sleep(parseRetryAfter(res) * 1000);
      res = await fetch(url, { ...options, headers });
    }
    if (res.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed: ${res.status} ${res.statusText}`);
    }
    // Check content type for JSON
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return res.json();
    }
    return res.text() as unknown as T;
  }
}

export const api = new ApiClient();

export function useApiWatch<T>(url: string | null, options?: { poll?: boolean }): { data: T | null; loading: boolean; error: Error | null } {
  const [data, setData] = useState<T | null>(() => {
    if (url) return api.getCached<T>(url);
    return null;
  });
  const [error, setError] = useState<Error | null>(() => {
    if (url) return api.getError(url);
    return null;
  });

  useEffect(() => {
    if (!url) {
      setData(null);
      setError(null);
      return;
    }

    // Reset data to cached value for new URL (or null if not cached)
    const cached = api.getCached<T>(url);
    setData(cached);
    setError(api.getError(url));

    const cleanup = api.watch<T>(url, (newData) => {
      setData(newData);
      setError(api.getError(url));
    }, options);

    return cleanup;
  }, [url, JSON.stringify(options)]);

  return { data, loading: !data && !error && !!url, error };
}